import { FC } from "react";

import { Grid, Stack } from "@mui/material";
import { getCurrencyFromCountry } from "@simplyk/common";
import { useFormContext } from "react-hook-form";

import { useRateFieldName } from "../../hooks/useRateFieldName";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardAmountInput } from "@/dashboard-design-system/DashboardAmountInput";
import { DashboardDivider } from "@/dashboard-design-system/Divider";
import { DashboardInformationTooltip } from "@/dashboard-design-system/DashboardTooltip/DashboardInformationTooltip";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { StepTicketsFormData } from "@/features/LiveFormEditor/Steps/StepTickets/types";

interface PayWhatYouCanAmountFieldsProps {
  variant?: "default" | "inline";
}

export const PayWhatYouCanAmountFields: FC<PayWhatYouCanAmountFieldsProps> = ({ variant = "default" }) => {
  const { t } = useTranslate();
  const { locale } = useLocaleContext();
  const { organization } = useCurrentUserContext();
  const { control } = useFormContext<StepTicketsFormData>();

  const suggestedField = useRateFieldName("payWhatYouCanSuggestedAmount");
  const minimumField = useRateFieldName("payWhatYouCanMinimumAmount");
  const currency = getCurrencyFromCountry(organization?.country);

  if (variant === "inline") {
    return (
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={1}>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
          <DashboardTypography variant="caption" vibe="text-neutral-quiet" sx={{ whiteSpace: "nowrap" }}>
            {t("dashboard", "ticketing.rateOption.suggestPrice")}
          </DashboardTypography>
          <DashboardInformationTooltip
            title={t("dashboard", "ticketing.payWhatYouCanSuggestedAmountTooltip")}
            placement="top"
          />
          <Stack sx={{ minWidth: 64 }}>
            <DashboardAmountInput
              name={suggestedField}
              control={control}
              locale={locale}
              currency={currency}
              size="small"
              placeholder={t("dashboard", "ticketing.payWhatYouCanPlaceholder") || ""}
            />
          </Stack>
        </Stack>
        <DashboardDivider orientation="vertical" flexItem />
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
          <DashboardTypography variant="caption" vibe="text-neutral-quiet" sx={{ whiteSpace: "nowrap" }}>
            {t("dashboard", "ticketing.rateOption.minPrice")}
          </DashboardTypography>
          <DashboardInformationTooltip
            title={t("dashboard", "ticketing.payWhatYouCanMinimumAmountTooltip")}
            placement="top"
          />
          <Stack sx={{ minWidth: 64 }}>
            <DashboardAmountInput
              name={minimumField}
              control={control}
              locale={locale}
              currency={currency}
              size="small"
            />
          </Stack>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={1}>
      <Grid item xs={6} spacing={0.5}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Grid container alignItems="center" spacing={0.5}>
              <Grid item>
                <DashboardTypography variant="body2" vibe="text-neutral-moderate">
                  {t("dashboard", "ticketing.payWhatYouCanSuggestedAmount")}
                </DashboardTypography>
              </Grid>
              <Grid item>
                <DashboardInformationTooltip
                  title={t("dashboard", "ticketing.payWhatYouCanSuggestedAmountTooltip")}
                  placement="top"
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <DashboardAmountInput
              name={suggestedField}
              control={control}
              locale={locale}
              currency={currency}
              size="small"
              placeholder={t("dashboard", "ticketing.payWhatYouCanPlaceholder") || ""}
            />
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={6} spacing={0.5}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Grid container alignItems="center" spacing={0.5}>
              <Grid item>
                <DashboardTypography variant="body2" vibe="text-neutral-moderate">
                  {t("dashboard", "ticketing.payWhatYouCanMinimumAmount")}
                </DashboardTypography>
              </Grid>
              <Grid item>
                <DashboardInformationTooltip
                  title={t("dashboard", "ticketing.payWhatYouCanMinimumAmountTooltip")}
                  placement="top"
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <DashboardAmountInput
              name={minimumField}
              control={control}
              locale={locale}
              currency={currency}
              size="small"
            />
          </Grid>
        </Grid>
      </Grid>
    </Stack>
  );
};
